import type { ApiClient, TokenProvider } from './client';
import { withCallLog } from './call-log';
import { HttpApiClient } from './http-client';
import { MockApiClient } from './mock-client';

export type { ApiClient, TokenProvider } from './client';
export { ApiError, isApiError, planRefusalOf, PLAN_CAPABILITY_REQUIRED, PLAN_QUOTA_EXCEEDED, type PlanRefusal } from './errors';
export { ApiProvider, useApi } from './api-context';
export { queryKeys } from './query-keys';
export { applyAskStreamEvent, emptyAskStreamAccum, type AskStreamAccum } from './ask-stream';
export {
  askProgressMessage,
  runAskAsync,
  ASK_POLL_INTERVAL_MS,
  ASK_POLL_TIMEOUT_MS,
  ASK_PROGRESS_FALLBACK,
  type AskRunCallbacks,
  type AskRunResult,
} from './ask-polling';
export * from './hooks';
export * from './types';

/** True when the build is configured to run against in-memory mocks. */
export const USE_MOCKS = import.meta.env.VITE_USE_MOCKS === 'true';

/**
 * Build the active client. Mock mode ignores token/baseUrl. Real mode reads
 * VITE_API_BASE_URL and pulls a fresh token per request from the auth layer.
 */
export function createApiClient(getToken: TokenProvider): ApiClient {
  if (USE_MOCKS) {
    // The token goes to the MOCK too, and for the same reason the real client
    // gets it: it is where the caller's ROLE comes from. Offline that token is
    // the dev-auth one, whose e-mail decides the role the console renders — so
    // handing it over is what keeps the mock's role refusals reachable instead
    // of leaving it a permanent seeded admin.
    const client = new MockApiClient({ getToken });
    // The e2e suite reads every call back (see call-log.ts).
    return import.meta.env.MODE === 'e2e' ? withCallLog(client) : client;
  }
  // Same-origin by default: the API is served under /v1 on the SAME CloudFront
  // distribution as the SPA (every alias: www, apex, *.cloudfront.net). An empty
  // or "/" base yields RELATIVE requests that always match the page origin and the
  // CSP 'self'. Set an absolute URL only to target a different host (then add it to
  // the CloudFront CSP connect-src).
  const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';
  return new HttpApiClient({ baseUrl, getToken });
}
